import { useState } from "react";
import axios from "axios";
import { Check, CalendarCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ThankYou = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | linked | notfound | error

  const link = async (e) => {
    e.preventDefault();
    if (!email || state === "sending") return;
    setState("sending");
    try {
      const res = await axios.post(`${API}/public/booked`, { email });
      setState(res.data.found ? "linked" : "notfound");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#FAF7F2] text-[#2C221E] flex flex-col items-center selection:bg-[#B25E3B]/20">
      <header className="w-full max-w-xl mx-auto flex items-center justify-center py-6 px-4">
        <a href="https://www.grayrootssociety.com" target="_blank" rel="noopener noreferrer" data-testid="booked-brand-link">
          <img src="/banner.png" alt="Gray Roots Society — Head Up, Crown On" className="h-12 w-auto" />
        </a>
      </header>
      <main className="w-full max-w-xl mx-auto px-4 sm:px-6 py-8 flex flex-col flex-1 justify-center">
        <div data-testid="booked-screen" className="animate-fade-up text-center">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#EDF3F0] mb-6">
            <CalendarCheck className="w-8 h-8 text-[#415A4F]" />
          </span>
          <h1 data-testid="booked-headline" className="font-serif-display text-3xl sm:text-4xl leading-[1.15] tracking-tight mb-4">
            It's official — your next chapter has a date.
          </h1>
          <p className="text-base sm:text-lg text-[#5A4D46] leading-relaxed mb-8 max-w-md mx-auto">
            Your Reinvention Kickstarter is booked. Ninety minutes, one-to-one,
            entirely about you — bring your first step, and we'll build the other
            twenty-nine around your real life.
          </p>
        </div>

        <div className="animate-fade-up stagger-2 rounded-3xl bg-white border border-[#E9DFD6] p-6 sm:p-8 text-left">
          {state === "linked" ? (
            <div data-testid="booked-linked-success" className="flex items-start gap-3">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#EDF3F0]">
                <Check className="w-4 h-4 text-[#415A4F]" />
              </span>
              <div>
                <p className="font-semibold text-[#2C221E]">All linked up.</p>
                <p className="text-sm text-[#85746C] mt-1">
                  Your quiz result and your booking are connected — we won't nudge you about booking again.
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="font-semibold text-[#2C221E] mb-1">One quick thing</p>
              <p className="text-sm text-[#85746C] mb-4">
                If you took the quiz, tell us the email you used — we'll connect it to your booking
                so your result and roadmap stay together.
              </p>
              <form onSubmit={link} className="flex flex-col gap-3">
                <input
                  data-testid="booked-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email you booked with"
                  className="w-full rounded-xl border border-[#E9DFD6] bg-[#FCF9F5] px-4 py-3.5 text-base text-[#2C221E] placeholder:text-[#85746C]/70 focus:outline-none focus:ring-2 focus:ring-[#B25E3B] focus:border-transparent"
                />
                <button
                  data-testid="booked-link-btn"
                  type="submit"
                  disabled={state === "sending"}
                  className="w-full rounded-full bg-[#B25E3B] hover:bg-[#9C4E2D] text-[#FAF7F2] font-semibold px-6 py-3.5 transition-colors duration-200 active:scale-[0.98] disabled:opacity-60"
                >
                  {state === "sending" ? "Linking..." : "Link my booking"}
                </button>
                {state === "notfound" && (
                  <p data-testid="booked-notfound" className="text-sm text-[#85746C]">
                    We couldn't find a quiz result under that email — no trouble, your booking stands either way.
                  </p>
                )}
                {state === "error" && (
                  <p data-testid="booked-error" className="text-sm text-[#B25E3B]">
                    Something didn't go through — please try again.
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      </main>
      <footer className="w-full max-w-xl mx-auto px-4 sm:px-6 py-6 text-center">
        <p className="text-xs text-[#85746C]">Head up, crown on · Gray Roots Society</p>
      </footer>
    </div>
  );
};
